import React, { memo, useCallback, useRef, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { CircularProgress } from '@mui/material'
import { useProcessContext } from '../../../store.js'
import { containsKeys } from '../utils/filterUtils.js'
import FieldHistory from '../../../components/DataEntry/FieldHistory.js'
import { replacePlaceholders } from '../utils/placeholderUtil.js'

const AutoCompleteField = ({
  field,
  fieldId,
  value,
  allValues,
  listDataIdMetadataMap,
  options = [],
  onChange,
  className = '',
  disabled = false,
  label = null,
  placeholder = null,
  style = {},
  helperText = null,
  autoFocus = false,
  isPivotDataLoading = false,
  getOptionLabel = (option) => option.value,
  inputSx = {},
}) => {
  const processContext = useProcessContext((state) => state.processContext)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const inputRef = useRef(null)

  const handleInputChange = (event, value, reason) => {
    onChange(event, value, reason)
  }

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }
  const handleDropdownClose = () => {
    setIsDropdownOpen(false)
  }

  const getActualValue = useCallback((value) => {
    if (!value) return value
    return { ...value, value: value.list_data_value || value.value || '' }
  }, [])
  const getResolvedValue = useCallback(
    (val) => {
      if (!val) return val
      let finalValue = val.list_data_value || val.value || ''
      if (finalValue.includes('[[')) {
        finalValue = replacePlaceholders(
          finalValue,
          allValues,
          listDataIdMetadataMap,
          processContext?.fieldMappings?.[field?.id] || field?.metadata?.placeholder_map || {},
          '',
          processContext?.fieldMetadata || {},
        )
      }
      return { ...val, value: finalValue }
    },
    [allValues, listDataIdMetadataMap, field, processContext],
  )
  const zigzagFilterOptions = useCallback((options, { inputValue }) => {
    return options.filter((option) => containsKeys(option?.value, inputValue))
  }, [])

  const autocompleteComponent = (
    <Autocomplete
      open={isDropdownOpen}
      isOptionEqualToValue={(option, value) =>
        value?.id ? option?.id === value?.id : option?.value === value?.value
      }
      options={options.map((it) => {
        if (value?.id && it.id == value.id) {
          return getResolvedValue(it)
        }
        return getActualValue(it)
      })}
      getOptionLabel={getOptionLabel}
      fullWidth
      style={style}
      className={className}
      disabled={disabled || isPivotDataLoading}
      sx={{
        '& .MuiInputBase-root': {
          height: '34.25px',
        },
        '& .MuiFormControl-root': {
          height: '34.25px',
        },
        ...inputSx,
      }}
      value={getResolvedValue(value)}
      onChange={handleInputChange}
      filterOptions={zigzagFilterOptions}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
      openOnFocus
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          variant="outlined"
          color="primary"
          size="small"
          label={label}
          autoFocus={autoFocus}
          placeholder={placeholder}
          error={helperText != null}
          // helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isPivotDataLoading ? <CircularProgress color="primary" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )

  if (!field || field.is_subfield) {
    return autocompleteComponent
  } else {
    return !isDropdownOpen ? (
      <FieldHistory
        fieldId={fieldId}
        field={field}
        dynamicListData={processContext.dynamicListData}
        processId={processContext?.processData?.id}
        allValues={allValues}
        helperText={helperText}
      >
        {autocompleteComponent}
      </FieldHistory>
    ) : (
      autocompleteComponent
    )
  }
}

export default memo(AutoCompleteField, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.options === nextProps.options &&
    prevProps.isPivotDataLoading === nextProps.isPivotDataLoading &&
    prevProps.helperText === nextProps.helperText &&
    prevProps.disabled === nextProps.disabled
  )
})
